import type Rate from '~/types/Rate';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { formatServiceLevelKey } from '~/types/CarrierAccount';
import { type RateObjectId } from '~/types/Rate';
import { attributePopularRateV2 } from '~/types/Rate';
import { type ShipmentObjectId } from '~/types/Shipment';

export type SelectedRates = Record<ShipmentObjectId, RateObjectId>;

export type RatesState = {
  readonly byShipmentId: Record<ShipmentObjectId, readonly RateObjectId[]>;
  readonly data: { [rateId: string]: Readonly<Rate> };
  readonly hiddenRates: readonly RateObjectId[];
  readonly selectedRates: SelectedRates;
  // Stores if a rate selection for a given shipment was made by the user or automatically by us
  readonly userTouchedRates: Record<ShipmentObjectId, boolean>;
};

const initialState: RatesState = {
  byShipmentId: {},
  data: {},
  hiddenRates: [],
  selectedRates: {},
  userTouchedRates: {},
};

const reducer = (state: RatesState = initialState, action: ActionTypes): RatesState => {
  switch (action.type) {
    case ActionKeys.SHIPMENT_FETCH_SUCCESS:
      const {
        shipment: { rates: ratesFromAPI },
      } = action.payload;
      const rates = attributePopularRateV2(ratesFromAPI);
      return {
        ...state,
        byShipmentId: {
          ...state.byShipmentId,
          [action.shipmentId]: rates.map((rate) => rate.objectId),
        },
        data: {
          ...state.data,
          ...rates.reduce((obj, rate) => ({ ...obj, [rate.objectId]: rate }), {}),
        },
      };
    case ActionKeys.MULTIPLE_RATE_SELECT:
      const newSelectedRates = Object.keys(action.payload.rateSelection).reduce<SelectedRates>(
        (obj, shipmentObjectId) => {
          const rateIds = state.byShipmentId[shipmentObjectId];
          const foundRateByServiceLevel =
            rateIds &&
            rateIds
              .map((rateId) => state.data[rateId])
              .find((rate) => {
                const {
                  carrierAccount,
                  servicelevel: { token },
                } = rate;
                const rateToken =
                  carrierAccount && !action.payload.shouldIgnoreCarrier
                    ? formatServiceLevelKey(carrierAccount, token)
                    : token;
                return rateToken === action.payload.serviceLevelKey;
              });

          if (foundRateByServiceLevel) {
            return {
              ...obj,
              [shipmentObjectId]: foundRateByServiceLevel.objectId,
            };
          }
          return {
            ...obj,
          };
        },
        {},
      );

      let newUserTouchedRates: Record<ShipmentObjectId, boolean> = {};
      for (const shipmentObjectId of Object.keys(newSelectedRates))
        newUserTouchedRates[shipmentObjectId] = true;

      return {
        ...state,
        selectedRates: {
          ...state.selectedRates,
          ...newSelectedRates,
        },
        userTouchedRates: {
          ...state.userTouchedRates,
          ...newUserTouchedRates,
        },
      };
    case ActionKeys.RATE_SELECT:
      return {
        ...state,
        selectedRates: {
          ...state.selectedRates,
          [action.payload.shipmentObjectId]: action.payload.rateObjectId,
        },
        userTouchedRates: {
          ...state.userTouchedRates,
          [action.payload.shipmentObjectId]: action.payload.userTriggered,
        },
      };
    case ActionKeys.RATE_CLEAR_SELECTION:
      return { ...state, selectedRates: {}, userTouchedRates: {} };
    case ActionKeys.RATE_SET_HIDDEN_RATES:
      return { ...state, hiddenRates: action.payload.hiddenRates };
    case ActionKeys.RATE_APPEND_HIDDEN_RATES:
      return { ...state, hiddenRates: [...state.hiddenRates, ...action.payload.hiddenRates] };
    case ActionKeys.RATE_CLEAR:
      const byShipmentId = { ...state.byShipmentId };
      const userTouchedRates = { ...state.userTouchedRates };
      const selectedRates = { ...state.selectedRates };

      action.payload.shipmentIds.forEach((id) => {
        delete byShipmentId[id];
        delete userTouchedRates[id];
        delete selectedRates[id];
      });

      return {
        ...state,
        byShipmentId,
        selectedRates,
        userTouchedRates,
      };

    default:
      return state;
  }
};

export default reducer;
